import Head from 'next/head';
import { GetServerSideProps } from 'next';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useEffect } from 'react';
import search from '@/algolia/search';
import PageModal from '@/components/General/PageModal';
import PostCard from '@/components/Stream/PostCard';
import StreamSortDropdown, {
  SortTypes,
} from '@/components/Stream/StreamSortDropdown';
import * as gtag from '@/lib/gtag';
import { IPost } from '@/types/Collections/posts';
import SearchForm from '@/components/Search/SearchForm';
import SearchTabs from '@/components/Search/SearchTabs';

const POSTS_PER_PAGE = 10;

interface IProps {
  results: {
    query: string;
    hits: IPost[];
    nbPages: number;
    page: number;
    nbHits: number;
    exhaustiveNbHits: boolean;
  };
  page: number;
  term: string;
  sortType: SortTypes;
}

const SearchResults = (props: IProps): JSX.Element => {
  const { results, sortType } = props;

  const router = useRouter();

  useEffect(() => {
    if (window) {
      let term = '';
      if (results && results.query) {
        term = results.query;
      }
      gtag.event({
        action: 'search',
        params: {
          term,
        },
      });
    }
  }, [results]);

  if (results && results.hits && results.hits.length) {
    return (
      <>
        <Head>
          <title>{`Search results ${
            results && results.query ? `for "${results.query}"` : ''
          } - Shift.ms`}</title>
          {results && results.query ? (
            <link
              rel="canonical"
              href={`${process.env.NEXT_PUBLIC_APP_DOMAIN}/search/${results.query}`}
            />
          ) : (
            ''
          )}
          <meta property="og:type" content="website" />
          <meta name="robots" content="noindex" key="robots" />
        </Head>
        <SearchForm />
        <div className="container is-narrow">
          <SearchTabs />
        </div>
        <div className="container is-narrow">
          <div className="is-flex is-flex-direction-row is-justify-content-space-between is-align-items-center">
            <StreamSortDropdown
              sortField={
                sortType === SortTypes.NewestPosts ? 'created' : 'lastActive'
              }
              searchTerm={router.query?.query?.[0]}>
              <div className="is-font-brand-bold">
                Sort {results.exhaustiveNbHits ? '' : 'approximately '}
                {results.nbHits} results by
              </div>
            </StreamSortDropdown>
            <p className="is-font-brand-bold">
              {results.page > 0 ? `Page ${results.page + 1}` : ''}
            </p>
          </div>
          <div>
            <div className="posts">
              {results.hits.map(post => (
                <PostCard key={post.id} post={post} />
              ))}
            </div>
          </div>
          <nav className="pagination" role="navigation" aria-label="pagination">
            {results.page > 0 ? (
              <Link
                href={`/search/${results.query}/${
                  sortType
                    ? `${sortType}/${results.page}`
                    : `${SortTypes.RecentActivity}/${results.page}`
                }`}
                className="pagination-previous"
                title="Previous">
                Previous
              </Link>
            ) : (
              <a className="pagination-previous is-disabled" title="Previous">
                Previous
              </a>
            )}
            {results.nbPages > results.page + 1 ? (
              <Link
                href={`/search/${results.query}/${
                  sortType
                    ? `${sortType}/${results.page + 2}`
                    : `${SortTypes.RecentActivity}/${results.page + 2}`
                }`}
                className="pagination-next">
                Next page
              </Link>
            ) : (
              <a className="pagination-next is-disabled">Next page</a>
            )}
          </nav>
          <PageModal parent="/search" />
        </div>
      </>
    );
  }
  return (
    <div className="container is-narrow">
      <Head>
        <title>{`Search results ${
          results && results.query ? `for "${results.query}"` : ''
        } - Shift.ms`}</title>
        {results && results.query ? (
          <link
            rel="canonical"
            href={`${process.env.NEXT_PUBLIC_APP_DOMAIN}/search/${results.query}`}
          />
        ) : (
          ''
        )}
        <meta property="og:type" content="website" />
      </Head>
      <div className="notification mt-5">
        <h1 className="title">Sorry, that search came up empty</h1>
        <p>
          Try another search, or <Link href="/home">browse the forum</Link>.
        </p>
      </div>
      <PageModal parent="/search" />
    </div>
  );
};

export default SearchResults;

export const getServerSideProps: GetServerSideProps = async ({ query }) => {
  const { query: params } = query;
  const [term, sortType = SortTypes.RecentActivity, pageNum = '1'] =
    params as Array<string | SortTypes>;

  const page = Number(pageNum);

  if (term) {
    let searchIndex = process.env.NEXT_PUBLIC_ALGOLIA_SEARCH_POSTS_INDEX;

    if (sortType === SortTypes.NewestPosts) {
      searchIndex = process.env
        .NEXT_PUBLIC_ALGOLIA_SEARCH_MOST_RECENT as string;
    }

    const data = await search(term, searchIndex, POSTS_PER_PAGE, page);

    if (data) {
      return {
        props: {
          results: data,
          page,
          term,
          sortType,
        },
      };
    }
  }
  return {
    props: {
      results: {},
      page: 1,
      term: '',
      sortType: SortTypes.RecentActivity,
    },
  };
};
